import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { useKits } from './useKits';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Trash2, Edit, Package, Minus, Search, PlusCircle, ChevronRight, ChevronDown, Filter } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Kit, KitItem } from '@/types/kit';
import { QuickAddInventoryItemForm } from './QuickAddInventoryItemForm';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { GearCategory } from '@/types/gear';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';

export function KitsManager() {
  const { t } = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [kitName, setKitName] = useState('');
  const [kitNotes, setKitNotes] = useState('');
  const [selectedItems, setSelectedItems] = useState<{ inventory_item_id: string; quantity: number; name: string; id?: string }[]>([]);
  const [editingKit, setEditingKit] = useState<Kit & { kit_items: KitItem[] } | null>(null);
  const [kitToDelete, setKitToDelete] = useState<string | null>(null);
  const [removedItemIds, setRemovedItemIds] = useState<string[]>([]);
  const [showNewItemForm, setShowNewItemForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useIsMobile();
  
  const { kits, createKit, updateKit, deleteKit } = useKits();

  // Fetch inventory items
  const { data: inventoryItems, refetch: refetchInventoryItems } = useQuery({
    queryKey: ['inventory-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .order('category')
        .order('name');
      
      if (error) {
        console.error('Error fetching inventory items:', error);
        toast.error(t('failed_load_inventory_items'));
        throw error;
      }
      
      return data || [];
    }
  });

  // Group inventory items by category
  const groupedInventoryItems = useMemo(() => {
    if (!inventoryItems) return {};
    
    const grouped: Record<string, any[]> = {};
    
    inventoryItems.forEach(item => {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });
    
    return grouped;
  }, [inventoryItems]);

  // Filter grouped items by search query
  const filteredGroupedItems = useMemo(() => {
    if (!searchQuery.trim()) return groupedInventoryItems;
    
    const filtered: Record<string, any[]> = {};
    const lowerQuery = searchQuery.toLowerCase();
    
    Object.entries(groupedInventoryItems).forEach(([category, items]) => {
      const filteredItems = items.filter(item => 
        item.name.toLowerCase().includes(lowerQuery) || 
        category.toLowerCase().includes(lowerQuery)
      );
      
      if (filteredItems.length > 0) {
        filtered[category] = filteredItems;
      }
    });
    
    return filtered;
  }, [groupedInventoryItems, searchQuery]);

  const handleCreateKit = () => {
    if (!kitName.trim()) {
      toast.error(t('kit_name_required'));
      return;
    }
    
    createKit({
      name: kitName,
      notes: kitNotes,
      items: selectedItems.map(item => ({
        inventory_item_id: item.inventory_item_id,
        quantity: item.quantity
      }))
    });
    
    resetForm();
    setIsCreateDialogOpen(false);
  };

  const handleEditKit = () => {
    if (!editingKit) return;
    if (!kitName.trim()) {
      toast.error(t('kit_name_required'));
      return;
    }

    updateKit({
      id: editingKit.id,
      name: kitName,
      notes: kitNotes,
      items: selectedItems.map(item => ({
        inventory_item_id: item.inventory_item_id,
        quantity: item.quantity
      })),
      removedItemIds
    });
    
    resetForm();
    setIsEditDialogOpen(false);
  };

  const handleDeleteKit = () => {
    if (kitToDelete) {
      deleteKit(kitToDelete);
      setKitToDelete(null);
      setIsDeleteAlertOpen(false);
    }
  };

  const openEditDialog = (kit: Kit & { kit_items: KitItem[] }) => {
    setEditingKit(kit);
    setKitName(kit.name);
    setKitNotes(kit.notes || '');
    setRemovedItemIds([]);
    
    // Map kit items to the format expected by our form
    const items = kit.kit_items.map(item => ({
      id: item.id,
      inventory_item_id: item.inventory_item_id,
      quantity: item.quantity,
      name: item.inventory_items?.name || 'Unknown item'
    }));
    
    setSelectedItems(items);
    setIsEditDialogOpen(true);
  };

  const openDeleteAlert = (kitId: string) => {
    setKitToDelete(kitId);
    setIsDeleteAlertOpen(true);
  };

  const resetForm = () => {
    setKitName('');
    setKitNotes('');
    setSelectedItems([]);
    setEditingKit(null);
    setRemovedItemIds([]);
    setShowNewItemForm(false);
    setSearchQuery('');
  };

  const addItemToKit = (item: { id: string; name: string }) => {
    const existingItemIndex = selectedItems.findIndex(selectedItem => selectedItem.inventory_item_id === item.id);
    if (existingItemIndex > -1) {
      const updatedItems = [...selectedItems];
      updatedItems[existingItemIndex].quantity += 1;
      setSelectedItems(updatedItems);
    } else {
      setSelectedItems([...selectedItems, { 
        inventory_item_id: item.id, 
        quantity: 1, 
        name: item.name 
      }]);
    }
  };

  const removeItemFromKit = (index: number) => {
    // If we're editing, keep track of removed item IDs to delete from database
    if (editingKit && selectedItems[index].id) {
      setRemovedItemIds([...removedItemIds, selectedItems[index].id as string]);
    }
    
    const updatedItems = [...selectedItems];
    updatedItems.splice(index, 1);
    setSelectedItems(updatedItems);
  };

  const updateItemQuantity = (index: number, newQuantity: number) => {
    if (newQuantity < 1) return;

    const updatedItems = [...selectedItems];
    updatedItems[index].quantity = newQuantity;
    setSelectedItems(updatedItems);
  };

  const increaseItemQuantity = (index: number) => {
    updateItemQuantity(index, selectedItems[index].quantity + 1);
  };

  const decreaseItemQuantity = (index: number) => {
    if (selectedItems[index].quantity > 1) {
      updateItemQuantity(index, selectedItems[index].quantity - 1);
    }
  };

  const handleCreateNewItem = async (newItemData: any) => {
    try {
      // Add the item to inventory
      const { data, error } = await supabase
        .from('inventory_items')
        .insert(newItemData)
        .select()
        .single();
      
      if (error) throw error;
      
      toast.success(t('item_added_to_inventory', { item: newItemData.name }));
      
      // Add the new item to the kit as well
      if (data) {
        addItemToKit({
          id: data.id,
          name: data.name
        });
      }
      
      // Refresh inventory items list
      refetchInventoryItems();
      setShowNewItemForm(false);
    } catch (error) {
      console.error('Error adding inventory item:', error);
      toast.error(t('failed_add_item_to_inventory'));
    }
  };

  const isItemSelected = (itemId: string) => {
    return selectedItems.some(item => item.inventory_item_id === itemId);
  };

  const renderCategoryItems = (category: string, items: any[]) => {
    return (
      <div className="space-y-2">
        {items.map(item => (
          <Button
            key={item.id}
            variant={isItemSelected(item.id) ? "secondary" : "outline"}
            size="sm"
            className={`justify-start w-full text-left ${
              isItemSelected(item.id) 
                ? 'bg-primary/20 border-primary' 
                : 'hover:bg-primary/10'
            }`}
            onClick={() => addItemToKit(item)}
          >
            <div className="flex justify-between w-full items-center">
              <span className="truncate">{item.name}</span>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                ${item.cost_per_unit || 0}/day
              </span>
            </div>
          </Button>
        ))}
      </div>
    );
  };

  // Component that contains the kit form content (shared between desktop/mobile)
  const KitFormContent = ({ isEdit = false, onSave }: { isEdit?: boolean, onSave: () => void }) => (
    <>
      <div className={`${isMobile ? "flex flex-col space-y-4" : "grid md:grid-cols-2 gap-4"}`}>
        {/* Kit Details */}
        <div>
          <Input 
            placeholder={t('kit_name_placeholder')} 
            value={kitName}
            onChange={(e) => setKitName(e.target.value)}
            className="mb-4"
          />
          
          <Textarea
            placeholder={t('kit_notes_placeholder')}
            value={kitNotes}
            onChange={(e) => setKitNotes(e.target.value)}
            className="mb-4"
          />
          
          {/* Selected Items */}
          <div className="overflow-auto max-h-[200px] border rounded-md mb-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('item')}</TableHead>
                  <TableHead>{t('qty')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                      {t('no_items_selected')}
                    </TableCell>
                  </TableRow>
                )}
                {selectedItems.map((item, index) => (
                  <TableRow key={item.inventory_item_id + index}>
                    <TableCell className="max-w-[140px] truncate">{item.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => decreaseItemQuantity(index)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <Input 
                          type="number" 
                          value={item.quantity}
                          min={1}
                          onChange={(e) => updateItemQuantity(index, parseInt(e.target.value) || 1)}
                          className="w-12 h-8 text-center px-0"
                        />
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => increaseItemQuantity(index)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => removeItemFromKit(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Inventory Item Selection */}
        <div>
          <h3 className="text-base font-semibold mb-2">{t('add_items')}</h3>
          
          <div className="mb-3 relative flex items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('search_inventory_items_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button 
              variant="outline" 
              size="icon"
              className="ml-2"
              onClick={() => setShowNewItemForm(true)}
              title={t('add_new_inventory_item')}
            >
              <PlusCircle className="h-4 w-4" />
            </Button>
          </div>
          
          <ScrollArea className={`${isMobile ? "h-[250px]" : "h-[350px]"} pr-1`}>
            {Object.keys(filteredGroupedItems).length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                {t('no_matching_items_found')}
              </div>
            ) : (
              <Accordion type="multiple" className="w-full space-y-2">
                {Object.entries(filteredGroupedItems).map(([category, items]) => (
                  <AccordionItem key={category} value={category} className="border bg-card/10 rounded-lg">
                    <AccordionTrigger className="px-3 py-2 hover:no-underline">
                      <div className="flex items-center gap-1">
                        <Filter className="h-3 w-3 text-primary/70" />
                        <span className="text-sm">{category}</span>
                        <span className="text-xs text-muted-foreground ml-1">({items.length})</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3">
                      {renderCategoryItems(category, items)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </ScrollArea>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button 
          variant="outline" 
          onClick={() => {
            resetForm();
            isEdit ? setIsEditDialogOpen(false) : setIsCreateDialogOpen(false);
          }}
        >
          {t('cancel')}
        </Button>
        <Button 
          onClick={onSave}
          disabled={!kitName || selectedItems.length === 0}
        >
          {isEdit ? t('save_changes') : t('create_kit')}
        </Button>
      </div>
    </>
  );

  return (
    <div className="space-y-4">
      {/* Header & Create Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{t('gear_kits')}</h2>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> {t('create_kit')}
        </Button>
      </div>

      {/* Existing Kits List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kits?.map(kit => (
          <div key={kit.id} className="bg-white/5 border border-white/10 p-4 rounded-lg relative group">
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => openEditDialog(kit)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => openDeleteAlert(kit.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <h3 className="font-semibold">{kit.name}</h3>
            <p className="text-sm text-white/70">{kit.notes}</p>
            <div className="mt-2">
              {kit.kit_items.map(item => (
                <div key={item.id} className="text-xs flex justify-between">
                  <span>{item.inventory_items?.name}</span>
                  <span>x{item.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {(!kits || kits.length === 0) && (
          <div className="col-span-full flex flex-col items-center justify-center gap-4 py-8 text-center">
            <Package className="h-12 w-12 text-white/30" />
            <div>
              <p className="text-lg font-medium text-white/70">{t('no_kits_created_yet')}</p>
              <p className="text-sm text-white/50">{t('create_first_kit_to_organize_gear')}</p>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> {t('create_kit')}
            </Button>
          </div>
        )}
      </div>

      {/* Mobile: Create Kit Drawer */}
      {isMobile ? (
        <Drawer open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DrawerContent className="max-h-[90vh]">
            <DrawerHeader className="border-b px-4 py-3">
              <DrawerTitle>{t('create_new_gear_kit')}</DrawerTitle>
            </DrawerHeader>
            <div className="p-4 overflow-auto">
              <KitFormContent onSave={handleCreateKit} />
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        /* Desktop: Create Kit Dialog */
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>{t('create_new_gear_kit')}</DialogTitle>
            </DialogHeader>
            <div className="overflow-auto flex-1 pr-1">
              <KitFormContent onSave={handleCreateKit} />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Mobile: Edit Kit Drawer */}
      {isMobile ? (
        <Drawer open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DrawerContent className="max-h-[90vh]">
            <DrawerHeader className="border-b px-4 py-3">
              <DrawerTitle>{t('edit_gear_kit')}</DrawerTitle>
            </DrawerHeader>
            <div className="p-4 overflow-auto">
              <KitFormContent isEdit={true} onSave={handleEditKit} />
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        /* Desktop: Edit Kit Dialog */
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>{t('edit_gear_kit')}</DialogTitle>
            </DialogHeader>
            <div className="overflow-auto flex-1 pr-1">
              <KitFormContent isEdit={true} onSave={handleEditKit} />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create New Item Dialog */}
      {showNewItemForm && (
        <Dialog open={showNewItemForm} onOpenChange={setShowNewItemForm}>
          <DialogContent className="sm:max-w-[550px]">
            <DialogHeader>
              <DialogTitle>{t('add_new_inventory_item')}</DialogTitle>
            </DialogHeader>
            <QuickAddInventoryItemForm 
              onClose={() => setShowNewItemForm(false)} 
              onSubmit={handleCreateNewItem}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('are_you_sure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_kit_warning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setKitToDelete(null)}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteKit} className="bg-destructive text-destructive-foreground">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
